import { useMount } from 'ahooks';
import { FC, Suspense, useMemo } from 'react';
import { I18nextProvider, useTranslation } from 'react-i18next';

import { AuthProvider } from '@/app/providers/AuthProvider';
import { QueryProvider } from '@/app/providers/QueryProvider';
import { ThemeProvider } from '@/app/providers/ThemeProvider';

import { AiSocketProvider } from '@/entities/ai-agent-chat';
import { useSettingsStore } from '@/entities/settings/models/store';
import { useSettings, useTimeDisplayFormat } from '@/entities/settings';
import { DEFAULT_API_HOST, setApiHostGetter, setServiceTokenGetter } from '@/shared/lib';
import { ErrorBoundary, MainLayoutProvider, Toaster } from '@/shared/ui';

import { AppRouter } from './providers/AppRouter/AppRouter';
import { logBuildInfo } from './logBuildInfo';
import { installColorMixFallback } from './colorMixFallback';
import { buildKitSettings } from './kitSettings';
import { IbsysKitProvider } from '@ibsys/kit';

logBuildInfo();
installColorMixFallback();

// Дочерний компонент рендерится внутри QueryProvider — buildKitSettings зависит
// от useTimeDisplayFormat, который использует useQuery и требует QueryClientProvider выше по дереву.
const AppProviders: FC = () => {
  const { language } = useSettings();
  const { t, i18n } = useTranslation(['common']);
  const playerTimeDisplayFormat = useTimeDisplayFormat('player');

  useMount(() => {
    i18n.changeLanguage(language);
    setApiHostGetter(() => useSettingsStore.getState().apiHost || DEFAULT_API_HOST);
    setServiceTokenGetter(() => useSettingsStore.getState().serviceToken);
  });

  const settings = useMemo(() => buildKitSettings(t, playerTimeDisplayFormat), [t, playerTimeDisplayFormat]);

  return (
    <ThemeProvider>
      <AuthProvider>
        <I18nextProvider i18n={i18n}>
          <Suspense fallback={<></>}>
            <IbsysKitProvider settings={settings}>
              <ErrorBoundary>
                <AiSocketProvider>
                  <MainLayoutProvider>
                    <AppRouter />
                  </MainLayoutProvider>
                </AiSocketProvider>
              </ErrorBoundary>
            </IbsysKitProvider>
          </Suspense>
        </I18nextProvider>
      </AuthProvider>
      {/* Вне провайдера авторизации: уведомления нужны и там, где приложение не рисуется, —
          под окном замены временного пароля и на экране входа */}
      <Toaster />
    </ThemeProvider>
  );
};

export const App: FC = () => {
  return (
    <QueryProvider>
      <AppProviders />
    </QueryProvider>
  );
};
